import { getDeviceGroupDevices } from '@/api';
import { AdvanceBreadcrumb } from '@/components/Breadcrumb';
import { useTableRequest } from '@/hooks';
import { ReloadOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Col, Form, Input, message, Row, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
import { DevicesType } from '@/api/devices';
import useAuthButton from '@/hooks/useAuthButton';
import { EditModal, EditModalRef } from '../DeviceManage/EditModal';
import BindDeviceModal from './components/BindDeviceModal';
import GroupTree from './components/GroupTree';

const { Text } = Typography;
const { useMessage } = message;

export interface DeviceGroupProps {}

const DeviceGroup: React.FC<DeviceGroupProps> = () => {
  // const navigate = useNavigate();
  const [messageApi, contextHolder] = useMessage();
  const [selectedTreeKeys, setSelectedTreeKeys] = useState<React.Key[]>([]);
  const [bindModalVisible, setBindModalVisible] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const editModalRef = useRef<EditModalRef>(null);

  // 使用 ref 保存最新的 selectedTreeKeys，以便在 onRequest 中访问
  const selectedTreeKeysRef = useRef<React.Key[]>([]);
  useEffect(() => {
    selectedTreeKeysRef.current = selectedTreeKeys;
  }, [selectedTreeKeys]);

  const { loading, form, dataSource, pagination, reload, search, reset } = useTableRequest<DevicesType>({
    onRequest: (data) => {
      // 没有选中节点时，不请求接口，直接返回空数据
      if (!selectedTreeKeysRef.current[0]) {
        return Promise.resolve({
          success: true,
          errorCode: 0,
          result: {
            list: [],
            total: 0,
            page: data.page || 1,
            pageSize: data.pageSize || 10,
          },
        });
      }

      return getDeviceGroupDevices({
        device_group_id: selectedTreeKeysRef.current[0] as string,
        page: data.page || 1,
        pageSize: data.pageSize || 10,
        name: data.name,
      });
    },
  });

  const { checkPermissionDisabled } = useAuthButton();

  // 当树节点选择改变时，重新加载表格数据
  useEffect(() => {
    reload();
  }, [selectedTreeKeys, reload]);

  // 打开绑定设备 Modal
  const handleOpenBindModal = (groupId: string) => {
    setSelectedGroupId(groupId);
    setBindModalVisible(true);
  };

  // 关闭绑定设备 Modal
  const handleCloseBindModal = () => {
    setBindModalVisible(false);
    setSelectedGroupId(null);
  };

  return (
    <>
      {contextHolder}
      <EditModal ref={editModalRef} onFinish={reload} />
      <AdvanceBreadcrumb title="设备分组" describe="通过设备分组，将设备按区域、项目或类型进行统一管理。" background={require('@/assets/images/product-background.png')} />
      <Row gutter={16} className="m20" wrap={false} style={{ marginRight: '0px', marginLeft: '0px' }}>
        <Col span={4} style={{ marginLeft: '20px', paddingLeft: '0px', paddingRight: '0px' }}>
          <GroupTree selectedKeys={selectedTreeKeys} onSelect={(keys) => setSelectedTreeKeys(keys)} onRefresh={reload} />
        </Col>
        <Col flex={1} style={{ paddingRight: '20px' }}>
          <Card size="default" style={{ marginBottom: '8px' }}>
            <style>
              {`
                .device-group-form {
                  display: flex !important;
                  flex-wrap: nowrap !important;
                  align-items: center;
                  overflow-x: auto;
                  white-space: nowrap;
                }
                .device-group-form .ant-form-item {
                  margin-right: 16px !important;
                  margin-bottom: 0 !important;
                  flex-shrink: 0 !important;
                  white-space: nowrap;
                }
                .device-group-form .ant-form-item-control {
                  flex-shrink: 0;
                }
              `}
            </style>
            <Form
              className="device-group-form"
              layout="inline"
              form={form}
              initialValues={{
                name: '',
              }}
            >
              <Form.Item key="name" name="name">
                <Input placeholder="设备名称"></Input>
              </Form.Item>
              <Form.Item>
                <Button type="primary" onClick={search} disabled={checkPermissionDisabled('gateway:device:query')}>
                  查询
                </Button>
              </Form.Item>
              <Form.Item>
                <Button type="default" onClick={reset}>
                  重置
                </Button>
              </Form.Item>
            </Form>
          </Card>
          <Card size="small">
            <Space className="mb15 justify-space-between">
              <div>
                <Button
                  type="primary"
                  disabled={checkPermissionDisabled('gateway:device:bind') || !selectedTreeKeys.length}
                  onClick={() => {
                    if (selectedTreeKeys.length > 0) {
                      handleOpenBindModal(selectedTreeKeys[0] as string);
                    } else {
                      messageApi.warning('请先选择一个分组');
                    }
                  }}
                >
                  设备绑定
                </Button>
              </div>
              <div>
                <Button
                  className="ml10"
                  disabled={checkPermissionDisabled('gateway:device:query')}
                  onClick={() => {
                    reload();
                  }}
                >
                  <ReloadOutlined />
                  刷新
                </Button>
              </div>
            </Space>
            <Table
              loading={loading}
              dataSource={dataSource}
              pagination={pagination}
              rowKey="id"
              size="small"
              scroll={{ x: 'max-content' }}
              columns={[
                {
                  title: '设备ID',
                  key: 'device_id',
                  dataIndex: 'device_id',
                  fixed: 'left',
                  render: (text: string) => text || '-',
                },
                {
                  title: '分组名称',
                  key: 'device_group_name',
                  dataIndex: 'device_group_name',
                  render: (text: string) => text || '-',
                },
                {
                  title: '设备名称',
                  key: 'device_name',
                  dataIndex: 'device_name',
                  fixed: 'left',
                  render: (text: string) => text || '-',
                },
                {
                  title: '设备状态',
                  key: 'status',
                  dataIndex: 'status',
                  render(value, record) {
                    return (
                      <>
                        <Badge
                          color={
                            {
                              在线: 'green',
                              离线: 'gray',
                              禁用: 'red',
                              未激活: 'orange',
                            }[record.status] || 'gray'
                          }
                        />
                        <Text className="ml5">{value || '未知'}</Text>
                      </>
                    );
                  },
                },

                {
                  title: '创建时间',
                  key: 'created',
                  dataIndex: 'created',
                  render(value) {
                    return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
                  },
                },

                // {
                //   title: '操作',
                //   key: 'action',
                //   fixed: 'right',
                //   dataIndex: 'action',
                //   render(value, record) {
                //     return (
                //       <Space split={<Text type="secondary">/</Text>}>
                //         <Typography.Link
                //           onClick={() => {
                //             const values = form.getFieldsValue();
                //             const query = new URLSearchParams({
                //               name: values.name || '',
                //             }).toString();
                //             navigate({ pathname: `/gateway/device/detail/${record?.id}`, search: query ? `?${query}` : '' });
                //           }}
                //         >
                //           详情
                //         </Typography.Link>
                //         <Typography.Link
                //           disabled={checkPermissionDisabled('gateway:device:edit')}
                //           onClick={() => {
                //             editModalRef.current?.open(record);
                //           }}
                //         >
                //           编辑
                //         </Typography.Link>
                //         <Popconfirm
                //           title="此操作将永久删除该条记录, 是否继续?"
                //           okText="确定"
                //           cancelText="取消"
                //           placement="topRight"
                //           disabled={checkPermissionDisabled('gateway:device:remove')}
                //           onConfirm={() => {
                //             deleteDevice(record?.id).then((resp) => {
                //               if (resp.success) {
                //                 messageApi.open({ type: 'success', content: '删除成功' });
                //                 reload();
                //               } else {
                //                 messageApi.open({ type: 'error', content: resp.errorMsg });
                //               }
                //             });
                //           }}
                //         >
                //           <Typography.Link disabled={checkPermissionDisabled('gateway:device:remove')}>删除</Typography.Link>
                //         </Popconfirm>
                //       </Space>
                //     );
                //   },
                // },
              ]}
            />
          </Card>
        </Col>
      </Row>
      <BindDeviceModal open={bindModalVisible} groupId={selectedGroupId} onCancel={handleCloseBindModal} onSuccess={reload} />
    </>
  );
};

export default DeviceGroup;
